import { Fragment } from 'react/jsx-runtime';

import { Tooltip, capitalize, makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { Text } from '@backstage/ui';

import { ActivityItem } from '@spotify/plugin-confidence-activity-react';

import {
  SchemaProperty,
  StructSchema,
  diffSchemas,
  parseSchemaProperties,
} from './types';

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateColumns: '3fr minmax(50px, auto)',
    gap: 'var(--bui-space-1)',
    alignItems: 'center',
  },
  propertyType: {
    textAlign: 'right',
  },
  added: {
    color: 'var(--bui-fg-success)',
  },
  removed: {
    color: 'var(--bui-fg-secondary)',
    textDecoration: 'line-through',
  },
});

export const FlagUpdatesSchemaDetails = ({
  oldSchema,
  newSchema,
}: {
  oldSchema: StructSchema | null | undefined;
  newSchema: StructSchema | null | undefined;
}) => {
  const classes = useStyles();

  // Show diff if both oldSchema and newSchema are present
  const showDiff = Boolean(oldSchema && newSchema);

  const properties = showDiff
    ? diffSchemas(
        parseSchemaProperties(oldSchema),
        parseSchemaProperties(newSchema),
      )
    : parseSchemaProperties(newSchema || oldSchema);

  const renderItem = (props: SchemaProperty[], depth: number) => {
    return props.map(property => {
      return (
        <Fragment key={`${property.name}-${depth}`}>
          <Tooltip title={property.name} arrow enterDelay={700}>
            <Text
              as="div"
              variant="body-medium"
              truncate
              className={classNames(
                showDiff && property.change === 'added' && classes.added,
                showDiff && property.change === 'removed' && classes.removed,
              )}
              style={{
                paddingLeft: `calc(var(--bui-space-2) * ${depth})`,
              }}
            >
              {property.name}
            </Text>
          </Tooltip>
          <Text
            as="div"
            variant="body-medium"
            className={classNames(
              classes.propertyType,
              showDiff && property.change === 'added' && classes.added,
              showDiff && property.change === 'removed' && classes.removed,
            )}
          >
            {capitalize(property.type)}
          </Text>
          {property.children && renderItem(property.children, depth + 1)}
        </Fragment>
      );
    });
  };

  return (
    <ActivityItem.Section label="Properties">
      <div className={classes.root}>
        {properties.length > 0 ? (
          renderItem(properties, 0)
        ) : (
          <Text variant="body-medium">No properties</Text>
        )}
      </div>
    </ActivityItem.Section>
  );
};
